import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useWithdrawalRequestsByEmail } from "./useWithdrawalRequestsByEmail.ts";
import { useUpsertWithdrawalRequest } from "./useUpsertWithdrawalRequest.ts";
import {
  WithdrawablePurchaseDTO,
  WithdrawalRequestDTO,
  WithdrawalRequestStatus,
} from "./WithdrawalRequestDTO.ts";

export function useWithdrawalRequestDate(customerEmail: string | undefined) {
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [existingRequest, setExistingRequest] =
    useState<WithdrawalRequestDTO | null>(null);
  const [purchases, setPurchases] = useState<WithdrawablePurchaseDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const { getByEmail } = useWithdrawalRequestsByEmail();
  const { createRequest, updateRequest } = useUpsertWithdrawalRequest();

  useEffect(() => {
    if (!customerEmail) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const result = await getByEmail(customerEmail);
      if (cancelled) {
        setLoading(false);
        return;
      }
      if (result) {
        const pending =
          result.requests.find(
            (r) => r.status === WithdrawalRequestStatus.Pending,
          ) ?? result.requests[0];
        setExistingRequest(pending ?? null);
        setPurchases(result.withdrawablePurchases);
        if (pending) {
          setDate(dayjs(pending.requestedAtUtc).format("YYYY-MM-DD"));
        }
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [customerEmail, getByEmail]);

  const persist = useCallback(async (): Promise<void> => {
    if (!customerEmail) return;
    const requestedAtUtc = new Date(date);

    try {
      if (existingRequest) {
        if (
          dayjs(existingRequest.requestedAtUtc).format("YYYY-MM-DD") === date
        ) {
          return;
        }
        await updateRequest(existingRequest.id, { requestedAtUtc });
        return;
      }

      const unambiguousPurchase =
        purchases.length === 1 ? purchases[0] : undefined;
      if (!unambiguousPurchase) return;

      await createRequest({
        userEmail: customerEmail,
        purchaseKey: unambiguousPurchase.purchaseKey,
        requestedAtUtc,
        notes: "Creata automaticamente dal rimborso in checkout-app",
        notify: false,
      });
    } catch (e) {
      console.error("Could not save the withdrawal request date", e);
    }
  }, [
    customerEmail,
    date,
    existingRequest,
    purchases,
    createRequest,
    updateRequest,
  ]);

  return {
    date,
    setDate,
    existingRequest,
    hasWithdrawablePurchase: purchases.length > 0,
    loading,
    persist,
  };
}
